import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PAYOUT_METHOD_LABELS,
  PAYOUT_METHODS,
  PROSPECT_STATUS_LABELS,
  PROSPECT_STATUSES,
  type Paged,
  type PayoutMethod,
  type ProspectDTO,
  type ProspectStatus,
  type SellerDTO,
} from '@bifeai/shared';
import {
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  EmptyState,
  Field,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  PageHeader,
  Select,
  Skeleton,
  useToast,
} from '@bifeai/ui';
import { CircleCheck, Columns3, Copy, Ellipsis, Link2, List, Pencil, Plus, RotateCcw, Target, Trash2, XCircle } from 'lucide-react';
import { useState } from 'react';
import {
  copy,
  fieldsOf,
  parseAmount,
  LostDialog,
  Pager,
  ProspectDialog,
  ProspectStatusBadge,
  prospectWhat,
  RangeFilter,
  useRange,
} from '../components/sellers';
import { ProspectBoard } from '../components/ProspectBoard';
import { api, errorMessage } from '../lib/api';
import { formatDate, formatUsd } from '../lib/format';
import { useCan } from '../lib/session';

const today = () => new Date(Date.now() - 3 * 3_600_000).toISOString().slice(0, 10);

/** Prospectos que reportan los vendedores: link de pago, cobro confirmado a mano (crea el cliente) o perdido. */
export function ProspectsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const can = useCan();
  const manage = can('prospects.manage');
  const pay = can('commissions.pay');
  const r = useRange();
  const [sellerId, setSellerId] = useState('');
  const [status, setStatus] = useState<ProspectStatus | ''>('');
  const [editing, setEditing] = useState<ProspectDTO | 'new' | null>(null);
  const [losing, setLosing] = useState<ProspectDTO | null>(null);
  const [confirming, setConfirming] = useState<ProspectDTO | null>(null);
  const [link, setLink] = useState<{ p: ProspectDTO; url: string } | null>(null);
  const [view, setView] = useState<'board' | 'list'>('board');
  const sellers = useQuery({ queryKey: ['sellers'], queryFn: () => api.get<SellerDTO[]>('/platform/sellers') });
  const list = useQuery({
    queryKey: ['prospects', r.qs({ sellerId, status })],
    queryFn: () => api.get<Paged<ProspectDTO>>(`/platform/prospects?${r.qs({ sellerId, status })}`),
    enabled: view === 'list',
  });
  // Tablero: todo lo que sigue en juego (y lo ganado o perdido hace poco), de una vez. Se actualiza solo.
  const boardQs = new URLSearchParams({ board: '1', ...(sellerId ? { sellerId } : {}) }).toString();
  const board = useQuery({
    queryKey: ['prospects', 'board', boardQs],
    queryFn: () => api.get<Paged<ProspectDTO>>(`/platform/prospects?${boardQs}`),
    enabled: view === 'board',
    refetchInterval: 30_000,
  });
  const stage = useMutation({
    mutationFn: (m: { p: ProspectDTO; status: 'NEW' | 'CONTACTED' | 'DEMO' }) =>
      api.post(`/platform/prospects/${m.p.id}/stage`, { status: m.status }),
    onSuccess: refresh,
    onError: (err) => toast.error('No se pudo mover', errorMessage(err)),
  });
  function refresh() {
    return qc.invalidateQueries({ queryKey: ['prospects'] });
  }

  const makeLink = useMutation({
    mutationFn: (p: ProspectDTO) => api.post<{ url: string }>(`/platform/prospects/${p.id}/link`).then((x) => ({ p, url: x.url })),
    onSuccess: async (x) => {
      setLink(x);
      await refresh();
    },
    onError: (err) => toast.error('No se pudo armar el link', errorMessage(err)),
  });
  const reopen = useMutation({
    mutationFn: (p: ProspectDTO) => api.post(`/platform/prospects/${p.id}/reopen`),
    onSuccess: refresh,
    onError: (err) => toast.error('No se pudo reabrir', errorMessage(err)),
  });
  const del = useMutation({
    mutationFn: (p: ProspectDTO) => api.del(`/platform/prospects/${p.id}`),
    onSuccess: refresh,
    onError: (err) => toast.error('No se pudo borrar', errorMessage(err)),
  });

  const d = list.data;
  return (
    <>
      <PageHeader
        title="Prospectos"
        subtitle="Los posibles clientes tuyos, de los vendedores y las consultas del sitio. Arrastralos de etapa; el cliente se crea recién cuando paga."
        actions={
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button icon={view === 'board' ? <List /> : <Columns3 />} onClick={() => setView(view === 'board' ? 'list' : 'board')}>
              {view === 'board' ? 'Ver lista' : 'Ver tablero'}
            </Button>
            {manage && (
              <Button variant="primary" icon={<Plus />} onClick={() => setEditing('new')}>
                Cargar prospecto
              </Button>
            )}
          </div>
        }
      />
      {view === 'board' && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            <Select aria-label="A cargo de" value={sellerId} onChange={(e) => setSellerId(e.target.value)} style={{ width: 'auto', minWidth: 200 }}>
              <option value="">Todos</option>
              <option value="none">Míos (BifeAI directo)</option>
              {(sellers.data ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <span className="bf-cell-sub">Ganados y perdidos: los de los últimos 60 días. Para ver todo, usá la lista.</span>
          </div>
          {board.isPending ? (
            <Skeleton style={{ height: 320 }} />
          ) : (
            <ProspectBoard
              items={board.data?.items ?? []}
              admin={manage}
              showSeller
              onOpen={(p) => manage && p.status !== 'WON' && setEditing(p)}
              onStage={(p, status) => stage.mutate({ p, status })}
              onLink={(p) =>
                p.email ? makeLink.mutate(p) : (toast.error('Falta el mail', 'Cargale el mail para armar el link de pago.'), setEditing(p))
              }
              onLost={(p) => setLosing(p)}
              onDelete={manage ? (p) => del.mutateAsync(p) : undefined}
            />
          )}
        </>
      )}
      {view === 'list' && (
        <Card>
          <CardHeader
            title={
              <RangeFilter r={r}>
                <Select
                  aria-label="Vendedor"
                  value={sellerId}
                  onChange={(e) => {
                    setSellerId(e.target.value);
                    r.setPage(1);
                  }}
                  style={{ width: 'auto', minWidth: 180 }}
                >
                  <option value="">Todos los vendedores</option>
                  <option value="none">Míos (BifeAI directo)</option>
                  {(sellers.data ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label="Estado"
                  value={status}
                  onChange={(e) => {
                    setStatus(e.target.value as ProspectStatus | '');
                    r.setPage(1);
                  }}
                  style={{ width: 'auto', minWidth: 160 }}
                >
                  <option value="">Todos los estados</option>
                  {PROSPECT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {PROSPECT_STATUS_LABELS[s]}
                    </option>
                  ))}
                </Select>
              </RangeFilter>
            }
          />
          <CardBody flush>
            {list.isPending ? (
              <Skeleton style={{ height: 180 }} />
            ) : !d?.items.length ? (
              <EmptyState icon={<Target />} title="Sin prospectos" text="Cuando un vendedor cargue un posible cliente, aparece acá." />
            ) : (
              <div className="bf-table-wrap">
                <table className="bf-table">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Posible cliente</th>
                      <th>Zona</th>
                      <th>Quiere</th>
                      <th>Vendedor</th>
                      <th>Estado</th>
                      <th aria-label="Acciones" />
                    </tr>
                  </thead>
                  <tbody>
                    {d.items.map((p) => (
                      <tr key={p.id}>
                        <td className="bf-cell-sub">{formatDate(p.createdAt)}</td>
                        <td>
                          <span className="bf-cell-main">{p.business}</span>
                          <div className="bf-cell-sub">{[p.contactName, p.email, p.phone].filter(Boolean).join(' · ')}</div>
                        </td>
                        <td className="bf-cell-sub">{[p.locality, p.province].filter(Boolean).join(', ') || '—'}</td>
                        <td>
                          <span className="bf-cell-sub">{prospectWhat(p)}</span>
                          <div className="bf-cell-sub">
                            {p.mode === 'SUBSCRIPTION'
                              ? `Hoy ${formatUsd(p.todayCents)} · después ${formatUsd(p.monthlyCents)}/mes`
                              : `Hoy ${formatUsd(p.todayCents)} · ${formatUsd(p.yearlyCents)}/año desde el 2.º año`}
                          </div>
                        </td>
                        <td className="bf-cell-sub">{p.sellerName ?? 'BifeAI (vos)'}</td>
                        <td>
                          <ProspectStatusBadge status={p.status} />
                          {p.lostReason && <div className="bf-cell-sub">{p.lostReason}</div>}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {(manage || pay) && p.status !== 'WON' && (
                            <Menu>
                              <MenuTrigger asChild>
                                <Button size="sm" variant="ghost" aria-label={`Acciones de ${p.business}`} icon={<Ellipsis />} />
                              </MenuTrigger>
                              <MenuContent>
                                {p.status !== 'LOST' && manage && (
                                  <MenuItem icon={<Link2 />} onSelect={() => makeLink.mutate(p)}>
                                    {p.status === 'LINK_SENT' ? 'Armar un link nuevo' : 'Link de pago (Stripe)'}
                                  </MenuItem>
                                )}
                                {p.checkoutUrl && (
                                  <MenuItem
                                    icon={<Copy />}
                                    onSelect={() =>
                                      void copy(p.checkoutUrl!).then((ok) => (ok ? toast.success('Link copiado') : toast.error('No se pudo copiar')))
                                    }
                                  >
                                    Copiar el link de pago
                                  </MenuItem>
                                )}
                                {p.status !== 'LOST' && pay && !(p.mode === 'SUBSCRIPTION' && p.offer !== 'BIFI') && (
                                  <MenuItem icon={<CircleCheck />} onSelect={() => setConfirming(p)}>
                                    Confirmar cobro a mano
                                  </MenuItem>
                                )}
                                {manage && (
                                  <MenuItem icon={<Pencil />} onSelect={() => setEditing(p)}>
                                    Editar
                                  </MenuItem>
                                )}
                                {p.status !== 'LOST' && manage && (
                                  <MenuItem icon={<XCircle />} onSelect={() => setLosing(p)}>
                                    Marcar perdido
                                  </MenuItem>
                                )}
                                {p.status === 'LOST' && manage && (
                                  <MenuItem icon={<RotateCcw />} onSelect={() => reopen.mutate(p)}>
                                    Reabrir
                                  </MenuItem>
                                )}
                                {manage && (
                                  <MenuItem icon={<Trash2 />} danger onSelect={() => del.mutate(p)}>
                                    Borrar
                                  </MenuItem>
                                )}
                              </MenuContent>
                            </Menu>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      )}
      {view === 'list' && d && <Pager page={d.page} total={d.total} pageSize={d.pageSize} onPage={r.setPage} />}

      {editing && (
        <ProspectDialog
          initial={editing === 'new' ? undefined : editing}
          sellers={sellers.data ?? []}
          onClose={() => setEditing(null)}
          onSave={async (body) => {
            if (editing === 'new') await api.post('/platform/prospects', body);
            else await api.put(`/platform/prospects/${editing.id}`, body);
            setEditing(null);
            toast.success('Prospecto guardado');
            await refresh();
          }}
        />
      )}
      {losing && (
        <LostDialog
          p={losing}
          onClose={() => setLosing(null)}
          onSave={async (reason) => {
            await api.post(`/platform/prospects/${losing.id}/lost`, { reason });
            setLosing(null);
            await refresh();
          }}
        />
      )}
      {confirming && (
        <ConfirmPaymentDialog
          p={confirming}
          onClose={() => setConfirming(null)}
          onDone={async () => {
            setConfirming(null);
            toast.success('Cobro confirmado', 'Se creó el cliente, le llegó su acceso y se generaron las comisiones.');
            await qc.invalidateQueries();
          }}
        />
      )}
      {link && (
        <Dialog open onOpenChange={(o) => !o && setLink(null)} title={`Link de pago para ${link.p.business}`}>
          <div className="bf-form">
            <Callout>
              Mandáselo al cliente (o lo ve su vendedor en su panel). Vence en 24 horas: si no paga, armá uno nuevo. Cuando paga, se crea su cuenta y
              la venta queda {link.p.sellerName ? `de ${link.p.sellerName}` : 'como venta directa de BifeAI'}.
            </Callout>
            <Input readOnly value={link.url} onFocus={(e) => e.target.select()} />
            <div className="bf-form-actions">
              <Button
                icon={<Copy />}
                onClick={() => void copy(link.url).then((ok) => (ok ? toast.success('Link copiado') : toast.error('No se pudo copiar')))}
              >
                Copiar
              </Button>
              <Button variant="primary" onClick={() => setLink(null)}>
                Listo
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

function ConfirmPaymentDialog({ p, onClose, onDone }: { p: ProspectDTO; onClose: () => void; onDone: () => Promise<void> }) {
  const [method, setMethod] = useState<PayoutMethod>('BANK');
  const [currency, setCurrency] = useState<'USD' | 'ARS'>('USD');
  const [amount, setAmount] = useState(String(p.todayCents / 100));
  const [fx, setFx] = useState('');
  const [paidOn, setPaidOn] = useState(today());
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const n = parseAmount;
  const usdCents = currency === 'USD' ? Math.round(n(amount) * 100) : n(fx) > 0 ? Math.round((n(amount) * 100) / n(fx)) : 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      await api.post(`/platform/prospects/${p.id}/confirm-payment`, {
        method,
        currency,
        amount: n(amount),
        fxRate: currency === 'ARS' ? n(fx) : null,
        paidOn,
        reference,
      });
      await onDone();
    } catch (err) {
      setErrors(fieldsOf(err));
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Confirmar cobro: ${p.business}`}>
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Callout>
          {prospectWhat(p)}. Precio de lista hoy: <strong>{formatUsd(p.todayCents)}</strong>. Al confirmar se crea el cliente, le llega su acceso por
          email{p.sellerName ? ` y se generan las comisiones de ${p.sellerName}` : ''}. El mantenimiento anual de este cliente se cobra a mano.
        </Callout>
        <div className="bf-form-row">
          <Field label="Cómo pagó">
            <Select value={method} onChange={(e) => setMethod(e.target.value as PayoutMethod)}>
              {PAYOUT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {PAYOUT_METHOD_LABELS[m]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Fecha del cobro" error={errors.paidOn}>
            <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
          </Field>
        </div>
        <div className="bf-form-row">
          <Field label="Moneda">
            <Select value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'ARS')}>
              <option value="USD">Dólares</option>
              <option value="ARS">Pesos</option>
            </Select>
          </Field>
          <Field label={currency === 'USD' ? 'Monto (USD)' : 'Monto (pesos)'} error={errors.amount}>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          {currency === 'ARS' && (
            <Field label="Dólar de ese día" hint="Pesos por dólar" error={errors.fxRate}>
              <Input inputMode="decimal" value={fx} onChange={(e) => setFx(e.target.value)} />
            </Field>
          )}
        </div>
        <div className="bf-cell-sub">
          Se registra como <strong>{formatUsd(usdCents)}</strong>
          {usdCents && usdCents !== p.todayCents ? ` (lista: ${formatUsd(p.todayCents)})` : ''}. Las comisiones se calculan sobre ese monto.
        </div>
        <Field label="Comprobante o referencia" hint="N° de operación, hash de la transacción…">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={busy} disabled={usdCents <= 0} onClick={() => void save()}>
            Confirmar y crear el cliente
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
